/* ══════════════════════════════════════════════════════════════════════════
   FMT reviews
   ────────────
   Three jobs, in one render path so they cannot fight each other:

   1. Clamp long quotes. Google reviews run to any length, and a 900-character
      review next to a 90-character one made the grid look broken.
   2. Lay the set out as columns, or as two continuously moving rows. Twelve
      reviews stacked vertically made the page a long scroll for content people
      skim; the marquee shows all of them in a fixed height.
   3. Optionally merge in live Google reviews.

   The third is strictly additive. Curated reviews are server-rendered, so every
   failure path, no key, bad place id, quota, CORS, outage, malformed response
, leaves the page as delivered. There is no loading state and no empty state,
   because there is never a moment with nothing to show.

   Everything re-renders through render(), so a Google merge and a marquee
   rebuild cannot end up fighting over the same DOM.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  var root = document.querySelector("[data-fmt-reviews]");
  if (!root) return;

  var mount = root.querySelector("[data-fmt-reviews-list]");
  var attr = root.querySelector("[data-fmt-attr]");
  if (!mount) return;

  var layout = root.dataset.layout === "marquee" ? "marquee" : "grid";
  var speed = parseInt(root.dataset.speed, 10) || 60;
  var rows = parseInt(root.dataset.rows, 10) === 2 ? 2 : 1;
  var max = parseInt(root.dataset.max, 10) || 9;
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  // The server-rendered cards are the canonical set. Everything else is a
  // re-render of this array, never an in-place patch.
  var cards = Array.prototype.slice.call(mount.children);

  /* ─── clamping ────────────────────────────────────────────────────────── */
  function fullTextFor(card) {
    var holder = card && card.querySelector("[data-fmt-review-full]");
    if (holder) {
      try {
        var t = JSON.parse(holder.textContent);
        if (t) return String(t);
      } catch (e) { /* fall through to the visible excerpt */ }
    }
    var q = card && card.querySelector(".fmt-review__q");
    return q ? q.textContent.trim() : "";
  }

  function openReview(card) {
    var section = card.closest(".fmt-sec") || document;
    var dlg = section.querySelector("[data-fmt-review-modal]");
    if (!dlg || !dlg.showModal) return false;

    var stars = card.querySelector(".fmt-stars");
    var slot = dlg.querySelector("[data-fmt-rm-stars]");
    if (slot && stars) {
      slot.innerHTML = stars.innerHTML;
      slot.setAttribute("aria-label", stars.getAttribute("aria-label") || "");
    }
    var by = card.querySelector(".fmt-review__by");
    var nameEl = by && by.querySelector("b");
    var n = dlg.querySelector("[data-fmt-rm-name]");
    if (n) n.textContent = nameEl ? nameEl.textContent.trim() : "";
    var m = dlg.querySelector("[data-fmt-rm-meta]");
    if (m) {
      var meta = by ? by.textContent.replace(nameEl ? nameEl.textContent : "", "").trim() : "";
      m.textContent = meta;
      m.hidden = !meta;
    }
    /* Real paragraphs rather than one pre-wrap block: a four paragraph review
       reads as four paragraphs. Built as elements from textContent, so nothing
       a reviewer typed can become markup. */
    var t = dlg.querySelector("[data-fmt-rm-text]");
    if (t) {
      t.textContent = "";
      var paras = fullTextFor(card).split(/\n\s*\n/);
      paras.forEach(function (para) {
        var line = para.replace(/\s*\n\s*/g, " ").trim();
        if (!line) return;
        var p = document.createElement("p");
        p.textContent = line;
        t.appendChild(p);
      });
      t.scrollTop = 0;
    }

    dlg.showModal();
    return true;
  }

  /* The button appears when the card is visibly clipped, and also when the full
     text is longer than the excerpt on the card -- on a wide column a short
     excerpt does not clamp, but there is still more review to read. */
  function applyClamp(el) {
    if (!el || el.dataset.clamped === "done") return;
    el.dataset.clamped = "done";

    var card = el.closest(".fmt-review");
    var clipped = el.scrollHeight - el.clientHeight >= 8;
    var hasMore = fullTextFor(card).length > el.textContent.trim().length + 8;
    if (!clipped && !hasMore) return;

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "fmt-review__more";
    btn.textContent = "Read full review";
    btn.addEventListener("click", function () {
      // Falls back to expanding in place where <dialog> is unsupported.
      if (openReview(card)) return;
      var open = el.classList.toggle("is-open");
      btn.textContent = open ? "Show less" : "Read full review";
    });
    el.insertAdjacentElement("afterend", btn);
  }

  function clampWithin(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll("[data-fmt-clamp]"), applyClamp);
  }

  function resetClamp(el) {
    el.dataset.clamped = "";
    el.classList.remove("is-open");
    var next = el.nextElementSibling;
    if (next && next.classList.contains("fmt-review__more")) next.remove();
  }

  /* ─── card construction ───────────────────────────────────────────────── */
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  function stars(n) {
    var out = "";
    for (var i = 1; i <= 5; i++) {
      out += '<svg viewBox="0 0 24 24" fill="' + (i <= n ? "currentColor" : "none") +
        '" stroke="currentColor" stroke-width="1.2" aria-hidden="true">' +
        '<path d="m12 2.6 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.4l-5.8 3.1 1.1-6.5L2.6 9.4l6.5-.9Z"/></svg>';
    }
    return out;
  }

  function card(r) {
    var fig = document.createElement("figure");
    fig.className = "fmt-card fmt-review";
    fig.innerHTML =
      '<div class="fmt-stars" role="img" aria-label="' + (r.rating || 5) + ' out of 5 stars">' + stars(r.rating || 5) + "</div>" +
      '<blockquote class="fmt-review__q" data-fmt-clamp>' + esc(r.text) + "</blockquote>" +
      '<script type="application/json" data-fmt-review-full>' + JSON.stringify(r.text) + "<\/script>" +
      '<figcaption class="fmt-review__by"><b>' + esc(r.author) + "</b>" + esc(r.when) + "</figcaption>";
    return fig;
  }

  /* ─── render ──────────────────────────────────────────────────────────── */
  function renderGrid(set) {
    var el = document.createElement("div");
    el.className = "fmt-reviews__masonry";
    el.setAttribute("data-fmt-reviews-list", "");
    set.forEach(function (c) { el.appendChild(c); });
    return el;
  }

  function renderMarquee(set) {
    var el = document.createElement("div");
    el.className = "fmt-marquee";
    el.setAttribute("data-fmt-reviews-list", "");

    var groups;
    if (rows === 2) {
      var half = Math.ceil(set.length / 2);
      groups = [set.slice(0, half), set.slice(half)];
    } else {
      groups = [set];
    }

    groups.forEach(function (group, i) {
      if (!group.length) return;
      var row = document.createElement("div");
      row.className = "fmt-marquee__row";
      var track = document.createElement("div");
      track.className = "fmt-marquee__track" + (i === 1 ? " is-reverse" : "");
      /* Seconds per loop is scaled by how many cards are in the row. Without
         this, moving from two rows of thirteen to one row of twenty-six would
         double the distance in the same time and the cards would sail past.
         Twelve is the reference the default was tuned against. */
      var dur = Math.max(20, Math.round(speed * group.length / 12));
      track.style.setProperty("--dur", dur + "s");

      group.forEach(function (c) { track.appendChild(c); });
      // A second copy makes the wrap seamless: the track travels exactly half
      // its own width, at which point copy two sits where copy one began.
      group.forEach(function (c) {
        var clone = c.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        // The clone must not carry duplicate interactive controls.
        var btn = clone.querySelector(".fmt-review__more");
        if (btn) btn.remove();
        var q = clone.querySelector("[data-fmt-clamp]");
        if (q) q.removeAttribute("data-fmt-clamp");
        track.appendChild(clone);
      });

      row.appendChild(track);
      el.appendChild(row);
    });
    return el;
  }

  function render() {
    var set = cards.slice(0, max);
    // Clamp before layout: the marquee clones cards, and cloning a card whose
    // toggle has not been decided yet produces clones without one.
    set.forEach(function (c) {
      var q = c.querySelector("[data-fmt-clamp]");
      if (q) resetClamp(q);
    });

    var useMarquee = layout === "marquee" && set.length >= 4;
    var next = useMarquee ? renderMarquee(set) : renderGrid(set);

    root.classList.toggle("fmt-reviews--marquee", useMarquee);
    root.classList.toggle("is-static", useMarquee && reduceMotion.matches);

    mount.replaceWith(next);
    mount = next;
    clampWithin(mount);
  }

  /* ─── optional Google hydration ───────────────────────────────────────── */
  var placeId = (root.dataset.placeId || "").trim();
  var apiKey = (root.dataset.apiKey || "").trim();

  function hydrateFromGoogle() {
    if (!placeId || !apiKey) return;

    fetch("https://places.googleapis.com/v1/places/" + encodeURIComponent(placeId), {
      headers: {
        "X-Goog-Api-Key": apiKey,
        "X-Goog-FieldMask": "reviews,rating,userRatingCount"
      }
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Places API " + r.status);
        return r.json();
      })
      .then(function (data) {
        var mapped = ((data && data.reviews) || []).map(function (rv) {
          return {
            text: (rv.originalText && rv.originalText.text) || (rv.text && rv.text.text) || "",
            author: (rv.authorAttribution && rv.authorAttribution.displayName) || "Google reviewer",
            when: rv.relativePublishTimeDescription || "Google review",
            rating: rv.rating || 5
          };
        }).filter(function (r) { return r.text.length > 20; });

        if (!mapped.length) return;

        // Live reviews lead; curated ones fill out the rest, then one re-render.
        cards = mapped.map(card).concat(cards);
        render();

        if (attr) attr.hidden = false;

        var ratingEl = root.querySelector("[data-fmt-rating]");
        if (ratingEl && data.rating) ratingEl.textContent = Number(data.rating).toFixed(1);
        var labelEl = root.querySelector("[data-fmt-rating-label]");
        if (labelEl && data.userRatingCount) {
          labelEl.textContent = "Average of " + data.userRatingCount + " Google reviews";
        }
      })
      .catch(function (err) {
        console.warn("[fmt-reviews] live Google reviews unavailable, showing curated set.", err.message);
      });
  }

  render();
  hydrateFromGoogle();

  // A quote that fits in one column may overflow in three, so re-measure.
  var resizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      Array.prototype.forEach.call(mount.querySelectorAll("[data-fmt-clamp]"), function (el) {
        if (el.classList.contains("is-open")) return;
        resetClamp(el);
        applyClamp(el);
      });
    }, 150);
  });

  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener("change", function () {
      root.classList.toggle("is-static", layout === "marquee" && reduceMotion.matches);
    });
  }
})();
